import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, strokeWidth, typography } = themeVariables;

/* The image's aspect ratio, from the design's 308 x 385 card image. Stated once
   so the image and its placeholder cannot drift apart. */
const IMAGE_ASPECT_RATIO = '4 / 5';

/**
 * The card's elevation and the dimming over an out-of-stock image.
 *
 * The theme has no shadow token and only a full 70% scrim, so both are mixed
 * here from the default content colour: the colour stays a theme decision and
 * rethemes with it, leaving only the geometry and the design's own percentages
 * stated locally. Add `shadow.base` and a lighter `overlay` token if a second
 * component needs either.
 *
 * The design specifies #0E0E11 where this uses the brand black, so both come out
 * a shade lighter and warmer. Imperceptible at 10%; well within tolerance at 30%.
 */
const ELEVATION = `0 0 4px 0 color-mix(in srgb, var(${colors.content.default}) 10%, transparent)`;
const DIM = `color-mix(in srgb, var(${colors.content.default}) 30%, transparent)`;

/**
 * The height the delivery block is held to: two lines of body `sm` plus the gap
 * between them, which is exactly what an estimated delivery occupies.
 *
 * Reserved rather than left to content so that a card showing "Out of stock" on
 * one line, or a wrapped special-event label, still lines its delivery row up
 * with its neighbours in a grid. A minimum rather than a fixed height, so
 * unexpected content grows the card instead of being clipped.
 */
const DELIVERY_MIN_HEIGHT = `calc(var(${typography.body.sizes.sm.lineHeight}) * 2 + var(${spacing.xxs}) / 2)`;

/* The card fills the width and the height it is given. No breakpoints here on
   purpose: it responds to its container, not to the viewport. The design's 308px
   is a reference width, not a constraint.

   `height: 100%` is what lets a row of cards in a grid line up: the card takes
   the full row height, the image and the delivery block keep fixed heights at
   either end, and the details between them absorb the difference. Against a
   parent of auto height it resolves to auto, so it is safe outside a grid too.

   `position: relative` is the anchor for the title link's overlay, which is what
   makes the whole card clickable through a single link. */
export const card = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: var(${borderRadius.md.mobile});
  background-color: var(${colors.surface.default});
  box-shadow: ${ELEVATION};
`;

/* Positioning context for the badge, the favourite button and the out-of-stock
   overlay, all of which sit over the image. Never shrinks: the image's height
   comes from the card's width via its aspect ratio, which is what makes it the
   same on every card in a row, and squashing it would break that. */
export const media = css`
  position: relative;
  flex-shrink: 0;
  line-height: 0;
`;

export const image = css`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  object-fit: cover;
`;

/* Keeps the card's height stable when a product arrives with no imagery, so a
   row of cards does not jump. */
export const imagePlaceholder = css`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  background-color: var(${colors.surface.disabled});
`;

/* Dims the image so an out-of-stock product reads as unavailable at a glance.
   Decorative: the wording below the divider carries the meaning. The strength is
   mixed into the colour rather than layered on with `opacity`, so two alphas are
   not compounded. */
export const outOfStockOverlay = css`
  position: absolute;
  inset: 0;
  background-color: ${DIM};
`;

/* The card owns where the badge sits, so the badge itself stays free of layout.
   Capped in width so a long label cannot run under the favourite button, which
   the design insets further from the edge than the badge. No z-index on purpose:
   the badge is not interactive, so the title link's overlay should sit above it
   and a tap on the badge should still open the product. */
export const badgeSlot = css`
  position: absolute;
  top: var(${spacing.xs});
  left: var(${spacing.xs});
  display: flex;
  max-width: calc(100% - var(${spacing.xs}) - var(${spacing.sm}));
`;

/* Above the title link's overlay, so favouriting does not navigate. */
export const favouriteSlot = css`
  position: absolute;
  right: var(${spacing.sm});
  bottom: var(${spacing.sm});
  z-index: 2;
  display: flex;
`;

/* Takes whatever height is left once the image has had its share, so the card
   below the image is what stretches in a grid rather than the image.

   One `sm` value does all of the card's spacing below the image: as padding it
   sets the gap from the image to the partner name, the inset either side, and the
   card's bottom edge; as the gap between this column's three children it sets the
   space above and below the divider. The divider therefore also stops `sm` short
   of each edge, which is how the design draws it. */
export const body = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.sm});
  min-height: 0;
  padding: var(${spacing.sm});
`;

/* Everything between the image and the delivery block. This is the part that
   flexes, so a one-line title and a two-line title still leave their cards'
   delivery rows level with each other. Content stays at the top; the slack
   collects underneath it. */
export const details = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.xxs});
`;

/* Held to a consistent height and centred within it, so the delivery row sits on
   the same line across a row of cards whether it shows a date, a wrapped event
   label, or "Out of stock". */
export const deliverySlot = css`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  min-height: ${DELIVERY_MIN_HEIGHT};
`;

export const partner = css`
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.link});
`;

/* The heading typeface, with its weight and tighter letter-spacing, on a body
   size. The card title is a heading and should read as one, but the heading type
   scale starts at 18px where the design's title is 15px, so the size comes from
   the body scale. Mixing the two groups is deliberate; the alternative is a
   title a size larger than the design and a taller card. Add a heading `xxs`
   step and this can take the whole group. */
export const title = css`
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.xs.fontSize});
  line-height: var(${typography.heading.sizes.xs.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

/**
 * One link per card, as before, but the card now has content above and below the
 * title that cannot sit inside an anchor: the badge, the favourite button and the
 * partner name. So the link stretches over the whole card with a pseudo-element
 * instead of wrapping it. Screen readers get a single link named by the title,
 * and the image is still tappable.
 */
export const titleLink = css`
  color: inherit;
  text-decoration: none;

  &::after {
    position: absolute;
    inset: 0;
    z-index: 1;
    content: '';
  }

  &:focus-visible::after {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: -2px;
  }
`;

/* The promotion needs a code typing in, so it earns the emphasis of its own
   tinted row. */
export const promotion = css`
  margin: 0;
  padding: var(${spacing.xxs}) var(${spacing.xs});
  border-radius: var(${borderRadius.md.mobile});
  background-color: var(${colors.surface.brand});
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.brand});
  text-align: center;
`;

export const promotionDiscount = css`
  font-weight: 700;
`;

/* No margin of its own: the body's gap sets the space either side of it. */
export const divider = css`
  flex-shrink: 0;
  margin: 0;
  border: 0;
  border-top: var(${strokeWidth}) solid var(${colors.stroke.default});
`;

/* Centred and in caps, replacing the delivery block: there is nothing to say
   about delivery for something the customer cannot buy. */
export const outOfStock = css`
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.secondary});
  text-align: center;
  text-transform: uppercase;
`;

/* Full bleed to the card's edges, so it reads as a label on the card rather than
   as another line of product copy. Sits below the delivery block, so a sponsored
   card's delivery row is one band higher than an unsponsored neighbour's. That
   follows from where the design puts it, and reserving an empty band on every
   other card to compensate would be worse. */
export const sponsored = css`
  flex-shrink: 0;
  margin: 0;
  padding: var(${spacing.xs});
  background-color: var(${colors.surface.disabled});
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.secondary});
  text-align: center;
`;
